// ProjectBoardPage.jsx — m3-05 "The Floor"
//
// Real three-column board wired to Task (working/needs_you/done) + AgentActivity.
// Live updates over SessionChannel (the proven observe reducer; no hand-rolled reducer).
// Tenant-scoped: company context from the page props, enforced server-side.
//
// IMPORTANT: No drag handlers exist — the system advances cards; the user resolves
// a Needs-you card by acting in the session drawer (m3-12, separate slice).
//
// For m3-05 the drawer opens a placeholder stub (the SessionDrawer is m3-12).

import React, { useEffect, useRef, useState, useCallback } from "react"
import { fetchData } from "../utils/api"
import consumer from "../channels/consumer"
import { shouldRefetchOnBoardFrame } from "../shell/companyBoardStream"
import SessionDrawer from "../shell/SessionDrawer.jsx"
import ShellIcon from "../shell/icons.jsx"
import VMark from "../shell/VMark.jsx"

// ── Channel color map (matches design tokens) ────────────────────────────────
const CH_COLORS = {
  calendars: "#A500C6",
  meta:      "#6A3AFB",
  social:    "#1FA84F",
  email:     "#FF7322",
  google:    "#4285F4",
  press:     "#E08600",
  vesta:     "#5349E5",
}

const CH_NAMES = {
  calendars: "Event Calendars",
  meta:      "Meta Ads",
  social:    "Social Media",
  email:     "Email Marketing",
  google:    "Google Ads",
  press:     "Press Releases",
  vesta:     "Vesta",
}

function chColor(ch) { return CH_COLORS[ch] || "#5349E5" }
function chName(ch)  { return CH_NAMES[ch]  || ch }

// ── ActorChip ────────────────────────────────────────────────────────────────
// M2: inline-SVG avatars per actor kind. agent=VMark, team=users SVG, auto=zap SVG,
// you=initials text. __av reduced 18px→16px per progress.css:30.
function ActorChip({ actor }) {
  const map = {
    agent: { label: "Vesta Agent", tag: "AI",         cls: "agent" },
    team:  { label: "Vesta Team",  tag: "Specialist",  cls: "team" },
    auto:  { label: "Auto-sync",   tag: "API",         cls: "auto" },
    you:   { label: "You",         tag: "",            cls: "you" },
  }
  const a = map[actor] || map.agent

  function ActorAv() {
    if (a.cls === "agent") return <VMark size={16} radius={8} />
    if (a.cls === "team")  return <ShellIcon name="users" size={10} />
    if (a.cls === "auto")  return <ShellIcon name="zap" size={10} />
    // "you" — show initials
    return <span style={{ fontSize: 7, fontWeight: 800, color: "#fff", lineHeight: 1 }}>RR</span>
  }

  return (
    <span className={`pgs-actor pgs-actor--${a.cls}`}>
      <span className="pgs-actor__av"><ActorAv /></span>
      <span className="pgs-actor__name">{a.label}</span>
      {a.tag && <span className="pgs-actor__tag">{a.tag}</span>}
    </span>
  )
}

// ── Card — single click target, no drag ──────────────────────────────────────
function Card({ className, taskRef, lit, style, onOpen, children }) {
  return (
    <div
      className={[className, lit ? "is-asklit" : ""].filter(Boolean).join(" ")}
      data-task-id={taskRef && taskRef.id}
      style={style}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen() }
      }}
    >
      {children}
    </div>
  )
}

// ── Working (Happening now) card ─────────────────────────────────────────────
function WorkingCard({ task, litTask, onOpen }) {
  const color = chColor(task.channel)
  const ref   = task.task_ref
  const pct   = task.pct || 0
  return (
    <Card
      className="pg4-now"
      taskRef={ref}
      lit={litTask === ref?.id}
      onOpen={() => onOpen(task)}
    >
      <div className="t">
        <span className="dot" style={{ background: color }} />
        {task.title.replace(/^\[DEMO\]\s*/i, '')}
      </div>
      {task.body && <div className="s">{task.body}</div>}
      <div className="bar">
        <i style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="ft">
        <ActorChip actor={task.actor || "agent"} />
        <span className="go" aria-hidden="true"><ShellIcon name="chevron-right" size={13} /></span>
      </div>
    </Card>
  )
}

// ── Needs-you card ───────────────────────────────────────────────────────────
function NeedsYouCard({ task, litTask, onOpen }) {
  const color = chColor(task.channel)
  const ref   = task.task_ref

  if (task.is_opp) {
    // Opportunity variant — tinted by channel color
    return (
      <Card
        className="pg4-hold is-opp"
        taskRef={ref}
        lit={litTask === ref?.id}
        style={{ "--oc": color }}
        onOpen={() => onOpen(task)}
      >
        <div className="eb">
          <ShellIcon name="sparkle" size={11} />
          Opportunity · {chName(task.channel)}
        </div>
        <div className="t">{task.title.replace(/^\[DEMO\]\s*/i, '')}</div>
        {task.body && <div className="s">{task.body}</div>}
        <div className="acts">
          <span className="b">{task.opp_cta || "See the plan"}</span>
        </div>
      </Card>
    )
  }

  return (
    <Card
      className="pg4-hold"
      taskRef={ref}
      lit={litTask === ref?.id}
      onOpen={() => onOpen(task)}
    >
      <div className="t">{task.title.replace(/^\[DEMO\]\s*/i, '')}</div>
      {task.body && <div className="s">{task.body}</div>}
      <div className="acts">
        <span className="b primary">{task.primary_label || "Take action"}</span>
        {task.secondary_label && (
          <span className="b">{task.secondary_label}</span>
        )}
      </div>
    </Card>
  )
}

// ── Done (Completed) card ────────────────────────────────────────────────────
function DoneCard({ task, litTask, onOpen }) {
  const color = chColor(task.channel)
  const ref   = task.task_ref
  const actorMap = { agent: "Vesta Agent", team: "Vesta Team", auto: "Auto-sync", you: "You" }
  const actorName = actorMap[task.actor] || "Vesta Agent"
  return (
    <Card
      className="pg4-out"
      taskRef={ref}
      lit={litTask === ref?.id}
      onOpen={() => onOpen(task)}
    >
      <i className="dot" style={{ background: color }} />
      <div className="bd">
        <span className="t">{task.title.replace(/^\[DEMO\]\s*/i, '')}</span>
        <span className="m">{task.time} · {actorName}</span>
      </div>
      <span className="go" aria-hidden="true"><ShellIcon name="chevron-right" size={13} /></span>
    </Card>
  )
}

// ── Column-mapping pure function (extracted for gate assertion) ───────────────
// Maps a board API response → { working, needsYou, done }.
// Opportunities appear in needs_you (they inflate the count by design).
export function mapBoardColumns(boardData) {
  return {
    working:  (boardData.working  || []).filter((t) => t.status === "working"),
    needsYou: (boardData.needs_you || []).filter((t) => t.status === "needs_you"),
    done:     (boardData.done     || []).filter((t) => t.status === "done"),
  }
}

// ── Item E: pure pagination helpers (exported for the column-map gate) ────────
// De-dupe accumulated done rows by task id, keeping FIRST occurrence (page order =
// recency order). Live refetch (replace) + Load more (append) can overlap; this
// prevents double cards.
export function dedupeById(rows) {
  const seen = new Set()
  const out = []
  for (const r of rows) {
    if (r == null || seen.has(r.id)) continue
    seen.add(r.id)
    out.push(r)
  }
  return out
}

// Live-refetch reconciliation: a TASK_STATE_CHANGE refetch re-requests max(40, loadedCount)
// with NO cursor and REPLACES done — a user who clicked "Load more" N times doesn't
// collapse back to 40. 40 = COMPLETED_FIRST_PAGE server-side (clamped at 200 there).
export function doneRefetchLimit(loadedCount) {
  return Math.max(40, loadedCount || 0)
}

// ── Live-update hook: re-fetch the board when a company-level task-state frame arrives ───
// m-r4-06: the board now subscribes to its OWN company's CompanyBoardChannel (the company-wide
// lane built in m-r4-05), replacing the pre-M-R4 single-session SessionChannel hack. A Task
// transition in ANY of the company's Sessions broadcasts a TASK_STATE_CHANGE frame, so a card
// produced by a different Session than the board last watched now refreshes the mounted board.

function useBoardData(companySlug) {
  const [boardData,   setBoardData]   = useState(null)  // working / needs_you / counts / meta
  const [doneRows,    setDoneRows]    = useState([])    // accumulated Completed rows (append + de-dupe)
  const [doneCursor,  setDoneCursor]  = useState(null)  // { updated_at, id } | null
  const [doneHasMore, setDoneHasMore] = useState(false)
  const [loading,     setLoading]     = useState(true)
  const [error,       setError]       = useState(null)
  const [loadingMore, setLoadingMore] = useState(false)

  // The loaded-count lives in a ref so fetchBoard stays referentially stable across
  // "Load more" appends (the stream hook holds the latest refetch in a ref anyway,
  // but a stable callback avoids re-running the initial-load effect).
  const loadedCountRef = useRef(0)

  // Page 1 (initial mount) OR live refetch. On live refetch we ask for max(40, loadedCount)
  // with NO cursor and REPLACE done — so a user who loaded N pages doesn't collapse back to 40.
  const fetchBoard = useCallback(async () => {
    try {
      const data = await fetchData(`api/agent/board`, {
        company_slug: companySlug,
        done_limit: doneRefetchLimit(loadedCountRef.current),
      })
      setBoardData(data)
      const rows = dedupeById(data.done || [])
      loadedCountRef.current = rows.length
      setDoneRows(rows)                                 // wholesale replace on page-1 / live refetch
      setDoneCursor(data.done_cursor || null)
      setDoneHasMore(Boolean(data.done_has_more))
      setError(null)
    } catch (err) {
      setError(err.message || "Failed to load board")
    } finally {
      setLoading(false)
    }
  }, [companySlug])

  const loadMoreDone = useCallback(async () => {
    if (!doneCursor || loadingMore) return
    setLoadingMore(true)
    try {
      const data = await fetchData(`api/agent/board`, {
        company_slug: companySlug,
        done_before: doneCursor.updated_at,
        done_before_id: doneCursor.id,
        done_limit: 20,
      })
      setDoneRows((prev) => {
        const merged = dedupeById([...prev, ...(data.done || [])])   // APPEND + de-dupe by id
        loadedCountRef.current = merged.length
        return merged
      })
      setDoneCursor(data.done_cursor || null)
      setDoneHasMore(Boolean(data.done_has_more))
    } catch (_e) {
      // leave has_more/cursor as-is so the user can retry; surfaced by the button staying visible
    } finally {
      setLoadingMore(false)
    }
  }, [companySlug, doneCursor, loadingMore])

  // Initial load
  useEffect(() => {
    fetchBoard()
  }, [fetchBoard])

  return { boardData, doneRows, doneHasMore, loading, error,
           refetch: fetchBoard, loadMoreDone, loadingMore }
}

// ── Company board stream: subscribe to CompanyBoardChannel and refetch on frame ─────
// On any TASK_STATE_CHANGE frame for the MOUNTED company, refetch the board view-model exactly
// once. The company_id equality check (shouldRefetchOnBoardFrame) ignores a frame for a different
// company — defense-in-depth on top of the server-side tenant gate (AC8). The refetch callback and
// mounted company id are held in refs so the subscription closure always sees the latest without
// re-subscribing on every render.
function useCompanyBoardStream(companySlug, mountedCompanyId, onTaskChange) {
  const onChangeRef  = useRef(onTaskChange)
  const companyIdRef = useRef(mountedCompanyId)
  onChangeRef.current  = onTaskChange
  companyIdRef.current = mountedCompanyId

  useEffect(() => {
    if (!companySlug) return undefined

    const subscription = consumer.subscriptions.create(
      { channel: "CompanyBoardChannel", company_slug: companySlug },
      {
        connected() {},
        disconnected() {},
        rejected() {},
        received(data) {
          if (shouldRefetchOnBoardFrame(data, companyIdRef.current)) onChangeRef.current()
        },
      }
    )
    return () => subscription.unsubscribe()
  }, [companySlug])
}

// ── Main board component ─────────────────────────────────────────────────────
function ProjectBoardPage({ companySlug, litTask, onAsk }) {
  const [openItem, setOpenItem]     = useState(null)

  const { boardData, doneRows, doneHasMore, loading, error, refetch, loadMoreDone, loadingMore } =
    useBoardData(companySlug)

  // The mounted company id (from the board view-model meta) gates which frames trigger a refetch.
  const mountedCompanyId = (boardData && boardData.meta && boardData.meta.company_id) || null

  // Live: subscribe to THIS company's board lane and refetch on a TASK_STATE_CHANGE frame.
  useCompanyBoardStream(companySlug, mountedCompanyId, refetch)

  if (loading) {
    return (
      <div className="pg4">
        <div className="pg4-board pg4-board--loading">
          <div className="pg4-loading">Loading board…</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="pg4">
        <div className="pg4-board pg4-board--error">
          <div className="pg4-error">Board error: {error}</div>
        </div>
      </div>
    )
  }

  const { working, needsYou } = mapBoardColumns(boardData || {})
  const needCount = needsYou.length

  // Item E: Completed = the ACCUMULATED keyset pages (40 first paint, +20 per "Load more"),
  // recency-ordered (updated_at DESC server-side), de-duped by id in useBoardData.
  const completedWindow = doneRows.filter((t) => t.status === "done")

  return (
    <div className="pg4">
      {/* M5: containerType moved to CSS .pgx-board-container rule (drop inline style) */}
      <div className="pgx-board-container">
        <div className="pg4-board">
          {/* Column 1 — Needs you (needs_you + opportunities) */}
          <section className="pg4-col">
            <div className="ch">
              <span className="dot hold" />
              Needs you
              {needCount > 0 && (
                <span className="n">{needCount}</span>
              )}
            </div>
            {/* M6: use .pg4-empty class, not inline style */}
            {needsYou.length === 0 && (
              <div className="pg4-empty">Nothing waiting on you right now</div>
            )}
            {needsYou.map((task) => (
              <NeedsYouCard
                key={task.id}
                task={task}
                litTask={litTask}
                onOpen={setOpenItem}
              />
            ))}
          </section>

          {/* Column 2 — Happening now (working) */}
          <section className="pg4-col">
            <div className="ch">
              <span className="dot run" />
              Happening now
            </div>
            {working.length === 0 && (
              <div className="pg4-now is-idle">
                <div className="t" style={{ color: "var(--vs-fg-3)", fontWeight: 500 }}>
                  All quiet — nothing running right now
                </div>
              </div>
            )}
            {working.map((task) => (
              <WorkingCard
                key={task.id}
                task={task}
                litTask={litTask}
                onOpen={setOpenItem}
              />
            ))}
          </section>

          {/* Column 3 — Completed (rolling window) */}
          <section className="pg4-col">
            <div className="ch">
              <span className="dot done" />
              Completed
            </div>
            <div className="pg4-stream">
              {/* M6: use .pg4-empty class, not inline style */}
              {completedWindow.length === 0 && (
                <div className="pg4-empty">No completed tasks yet</div>
              )}
              {completedWindow.map((task) => (
                <DoneCard
                  key={task.id}
                  task={task}
                  litTask={litTask}
                  onOpen={setOpenItem}
                />
              ))}
              {/* Item E: keyset "Load more" — styled per CLAUDE.md button rule (pgs-calbtn,
                  never a bare/gray native button). Hidden entirely when no more pages. */}
              {doneHasMore && (
                <button
                  type="button"
                  className="pgs-calbtn pg4-loadmore"
                  onClick={loadMoreDone}
                  disabled={loadingMore}
                  data-testid="board-load-more-done"
                >
                  {loadingMore ? "Loading…" : "Load more"}
                </button>
              )}
            </div>
          </section>
        </div>
      </div>

      {openItem && (
        <SessionDrawer item={openItem} onClose={() => setOpenItem(null)} onAsk={onAsk} />
      )}
    </div>
  )
}

export default ProjectBoardPage
